import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isApiError } from '../api';
import { riderApi } from '../api/riders';
import { vehicles as vehiclesApi } from '../api/vehicles';
import { clearDriverSession, getDriverSession } from '../lib/driverSession';
import { labelFor, useVehicleTypes } from '../lib/useVehicleTypes';
import DriverPermissionsPanel from '../components/driver/DriverPermissionsPanel';
import RiderFleetSection from '../components/driver/RiderFleetSection';
import { STATE_WORDS } from './DriverApplicationPage';
import './driverEarningsWalletProfile.css';
import './driverProfilePremium.css';
import './driverNotifications.css';


function initials(name) {
  const n = String(name || '').trim();
  if (!n) return '?';
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function IcInfo() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden className="dpr-infoIcon">
      <circle cx="12" cy="12" r="7.2" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M12 10.2V16M12 7.2v.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function IcLogout() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden className="dpr-logoutIcon">
      <path
        d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcFile() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden className="dpr-docIcon">
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M14 2v6h6M10 13h4M10 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IcVehicleType() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M5 17h14l-1-5H6l-1 5zM7 17a2 2 0 1 0 4 0M13 17a2 2 0 1 0 4 0M6 12l1.5-4h9L18 12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcWrench() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5 2.5 2.5 2.5-2.5-2.5-2.5 2.5-2.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcPlate() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IcPalette() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M12 3a9 9 0 1 0 0 18c2.5 0 3-2 3-2s-.5-2-2-2h-1a3 3 0 0 1-3-3V9a4 4 0 0 1 4-4h1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
      <circle cx="10" cy="7" r="1" fill="currentColor" />
      <circle cx="14" cy="7" r="1" fill="currentColor" />
    </svg>
  );
}

const VEHICLE_ROWS = [
  // The office's name for the rider's vehicle type (`useVehicleTypes`).
  { key: 'type', label: 'Type', Icon: IcVehicleType, value: (p, types) => labelFor(p.vehicle_type, types) || '—' },
  {
    key: 'make',
    label: 'Make & Model',
    Icon: IcWrench,
    value: (p) => [p.vehicle_make, p.vehicle_model].filter(Boolean).join(' ') || '—',
  },
  { key: 'plate', label: 'Plate', Icon: IcPlate, value: (p) => p.vehicle_plate || '—' },
  { key: 'color', label: 'Color', Icon: IcPalette, value: (p) => p.vehicle_colour || '—' },
];

function ProfileAvatar({ profile }) {
  return <div className="dpr-avInitials">{initials(profile.full_name)}</div>;
}

function IcBell() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M12 3a4.5 4.5 0 0 0-4.5 4.5V10l-1.2 2.4A1 1 0 0 1 7.2 14h9.6a1 1 0 0 1 .9-1.6L17 10V7.5A4.5 4.5 0 0 0 12 3Z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
      />
      <path d="M10.5 18a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IcChevron() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PremiumProfileShell({ children, profile }) {
  return (
    <div className="dpr dpr--premium" role="main" aria-label="Driver profile">
      <header className="dpr-nav">
        <span className="dpr-navSpacer" aria-hidden />
        <h1>Profile</h1>
        <span className="dpr-navSpacer" aria-hidden />
      </header>
      <div className="dpr-headerBand">
        <div className="dpr-av" aria-hidden>
          <ProfileAvatar profile={profile} />
        </div>
        <div className="dpr-identity">
          <h2 className="dpr-n">{profile.full_name || 'Driver'}</h2>
          <p className="dpr-p">{profile.email || '—'}</p>
        </div>
      </div>
      <div className="dpr-sc">{children}</div>
    </div>
  );
}

/**
 * The rider's profile, read from InGo's API (ingo-e0, 2 Oct 2026): who they
 * registered as and their vehicle (`rider/me`), and their papers as the office
 * left them (`rider/me/documents`). Read only: the office verified all of it,
 * so a change goes through support. A token the server refuses signs in
 * again; signing out ends the session on the server too.
 */
export default function DriverProfilePage({ riders = riderApi, fleet, vehicleTypes = vehiclesApi }) {
  const navigate = useNavigate();
  const { vehicles: vehicleTypeList } = useVehicleTypes(vehicleTypes);
  const [profile, setProfile] = useState(null);
  const [papers, setPapers] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const session = getDriverSession();
    if (!session?.id) {
      navigate('/driver/login', { replace: true });
      return undefined;
    }
    setProfile(session);
    let cancelled = false;
    const refused = (err) => {
      if (cancelled || !isApiError(err) || err.status !== 401) return;
      clearDriverSession();
      navigate('/driver/login', { replace: true });
    };
    riders.me().then((account) => {
      if (cancelled) return;
      setProfile({ ...account, full_name: [account.first_name, account.last_name].filter(Boolean).join(' ') });
      setLoading(false);
    }, refused);
    riders.documents().then(({ slots }) => !cancelled && setPapers(slots), refused);

    return () => {
      cancelled = true;
    };
  }, [navigate, riders]);

  const onLogout = async () => {
    await riders.signOut();
    clearDriverSession();
    navigate('/driver/login', { replace: true });
  };

  if (!profile && loading) {
    return (
      <PremiumProfileShell profile={{ full_name: 'Loading…', email: '' }}>
        <p className="dpr-infoText" style={{ margin: 0, textAlign: 'center', color: '#6b7280' }}>
          Loading profile…
        </p>
      </PremiumProfileShell>
    );
  }

  if (!profile) return null;

  return (
    <PremiumProfileShell profile={profile}>
      <Link to="/driver/notifications" className="dn-menuRow">
        <span className="dn-menuRow__icon" aria-hidden>
          <IcBell />
        </span>
        <span className="dn-menuRow__text">
          <span className="dn-menuRow__title">Notifications</span>
          <span className="dn-menuRow__sub">Manage offer alerts, ring, and push</span>
        </span>
        <span className="dn-menuRow__chev" aria-hidden>
          <IcChevron />
        </span>
      </Link>

      <DriverPermissionsPanel />

      <Link to="/driver/chat" className="dpr-chatBtn">
        Chat with support
      </Link>

      <div className="dpr-infoCard" role="note">
        <IcInfo />
        <p className="dpr-infoText">
          To change these details, contact support.
        </p>
      </div>

      <section className="dpr-section" aria-labelledby="dpr-contact-h">
        <h2 id="dpr-contact-h" className="dpr-secH">
          Contact &amp; ID
        </h2>
        <div className="dpr-secB dprRegCard">
          <div className="dprRegRow">
            <div className="dprRegLab">Phone</div>
            <div className="dprRegVal">{profile.phone || '—'}</div>
          </div>
          <div className="dprRegRow">
            <div className="dprRegLab">National ID / Passport</div>
            <div className="dprRegVal">{profile.national_id || '—'}</div>
          </div>
        </div>
      </section>

      <section className="dpr-section" aria-labelledby="dpr-vehicle-h">
        <h2 id="dpr-vehicle-h" className="dpr-secH">
          Vehicle
        </h2>
        <div className="dpr-secB dprRegCard">
          {VEHICLE_ROWS.map(({ key, label, Icon, value }) => (
            <div key={key} className="dpr-vehRow">
              <span className="dpr-vehIcon" aria-hidden>
                <Icon />
              </span>
              <div className="dpr-vehBody">
                <div className="dprRegLab">{label}</div>
                <div className="dprRegVal">{value(profile, vehicleTypeList)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <RiderFleetSection fleet={fleet} />

      <section className="dpr-section" aria-labelledby="dpr-docs-h">
        <h2 id="dpr-docs-h" className="dpr-secH">
          Documents you uploaded
        </h2>
        <div className="dpr-secB dprRegCard">
          {papers === null ? (
            <p className="dpr-docMuted" style={{ margin: 0 }}>
              Loading your papers…
            </p>
          ) : (
            papers.map((slot) => (
              <div key={slot.type} className="dpr-docRow">
                <IcFile />
                <span className="dpr-docName">{slot.type_label}</span>
                <span className="dpr-docMuted">{slot.document ? STATE_WORDS[slot.document.compliance_state] ?? '—' : 'Not sent'}</span>
              </div>
            ))
          )}
        </div>
        <Link to="/driver/application" className="dpr-docView" style={{ display: 'inline-block', marginTop: '0.5rem' }}>
          Send or replace a paper
        </Link>
      </section>

      <button type="button" className="dpr-logoutRow" onClick={onLogout} aria-label="Log out of driver app">
        <IcLogout />
        Log Out
      </button>
      {/* Google Play requires it in the app (InGo ADR 0038). */}
      <Link to="/driver/delete-account" className="dpr-deleteLink">
        Delete my account
      </Link>
    </PremiumProfileShell>
  );
}
